import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import { fetchProjects } from '../../services/projectService';
import { fetchSearchResults } from '../../services/publicTendersApi';
import { slugify } from '../../utils/slugify';
import RomaniaMap from '../../components/RomaniaMap/RomaniaMap';
import InviteFormModal from '../../components/InviteFormModal/InviteFormModal';
import { useAuth } from '../../context/AuthContext';
import {
  FileText,
  DollarSign,
  Activity,
  Folder,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    todayProcedures: 0,
    procedures7d: 0,
    totalMarketValue: 0,
    weeklyTrend: 0,
    activeProjects: 0,
    loading: true,
  });

  const [recentProcedures, setRecentProcedures] = useState([]);
  const [topAuthorities, setTopAuthorities] = useState([]);
  const [proceduresByType, setProceduresByType] = useState([]);
  const [trendChartData, setTrendChartData] = useState([]);
  const [regionalStats, setRegionalStats] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoadingData(true);

        // Calculate today's date at start of day for filtering
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayISO = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD

        // Fetch projects
        const projectsResponse = await fetchProjects({
          pageIndex: 0,
          pageSize: 50,
          sortBy: 'createdDate',
          sortOrder: 'desc',
        });

        // Fetch procedures WITHOUT date filter to get all recent procedures.
        // 500 instead of 100 so the sample spans enough days to make the
        // 7d-vs-prev-7d trend computation actually have a baseline.
        const proceduresResponse = await fetchSearchResults({
          pageIndex: 0,
          pageSize: 500,
        });

        // Fetch TODAY's procedures separately for accurate count
        const todayProceduresResponse = await fetchSearchResults({
          pageIndex: 0,
          pageSize: 100,
          publishedDate: todayISO, // Filter by today's date
        });

        console.log('=== API RESPONSES ===');
        console.log(
          'Procedures Response FULL:',
          JSON.stringify(proceduresResponse, null, 2),
        );
        console.log(
          'Today Procedures Response:',
          JSON.stringify(todayProceduresResponse, null, 2),
        );
        console.log(
          'Projects Response FULL:',
          JSON.stringify(projectsResponse, null, 2),
        );

        // Correct data mapping - API returns 'results' not 'items'
        let procedures = [];
        let todayProcedures = [];
        let projects = [];

        // Map procedures (all recent)
        if (
          proceduresResponse?.results &&
          Array.isArray(proceduresResponse.results)
        ) {
          procedures = proceduresResponse.results;
        } else if (Array.isArray(proceduresResponse)) {
          procedures = proceduresResponse;
        } else if (
          proceduresResponse?.items &&
          Array.isArray(proceduresResponse.items)
        ) {
          procedures = proceduresResponse.items;
        }

        // Map today's procedures
        if (
          todayProceduresResponse?.results &&
          Array.isArray(todayProceduresResponse.results)
        ) {
          todayProcedures = todayProceduresResponse.results;
        } else if (Array.isArray(todayProceduresResponse)) {
          todayProcedures = todayProceduresResponse;
        } else if (
          todayProceduresResponse?.items &&
          Array.isArray(todayProceduresResponse.items)
        ) {
          todayProcedures = todayProceduresResponse.items;
        }

        // Map projects
        if (
          projectsResponse?.results &&
          Array.isArray(projectsResponse.results)
        ) {
          projects = projectsResponse.results;
        } else if (Array.isArray(projectsResponse)) {
          projects = projectsResponse;
        } else if (
          projectsResponse?.items &&
          Array.isArray(projectsResponse.items)
        ) {
          projects = projectsResponse.items;
        }

        console.log('Mapped Procedures Array:', procedures);
        console.log('Procedures Count:', procedures.length);
        console.log('Mapped Today Procedures Array:', todayProcedures);
        console.log('Today Procedures Count:', todayProcedures.length);
        console.log('Mapped Projects Array:', projects);
        console.log('Projects Count:', projects.length);

        // Calculate today's date
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        const twoWeeksAgo = new Date(today);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

        // Count procedures by date - use API filtered count for today
        const todayCount = todayProcedures.length; // Accurate count from API with date filter
        let thisWeekCount = 0;
        let lastWeekCount = 0;
        let totalMarketValue = 0;

        const typeMap = {};
        const authorityMap = {};

        console.log('Starting to process procedures...');
        if (!Array.isArray(procedures) || procedures.length === 0) {
          console.warn('No procedures to process!');
        } else {
          console.log('Sample procedure data (first 3) - ALL FIELDS:');
          procedures.slice(0, 3).forEach((proc, i) => {
            console.log(`Procedure ${i + 1} - ALL KEYS:`, Object.keys(proc));
            console.log(`Procedure ${i + 1} - FULL OBJECT:`, proc);
          });
        }

        // Calculate project-procedure relationships
        let proceduresInProjects = 0;
        let todayProceduresInProjects = 0;

        procedures.forEach((proc) => {
          const procTitle = (proc.auctionTitle || '').toLowerCase();
          const procAuthor = (proc.authorityName || '').toLowerCase();
          const procDate = new Date(
            proc.publishedDate || proc.publicationDate || proc.createdDate,
          );
          procDate.setHours(0, 0, 0, 0);

          // More accurate matching: check if procedure matches any project
          const isInProject = projects.some((project) => {
            const projectTitle = (project.projectTitle || '').toLowerCase();
            const projectAuthor = (project.authorityName || '').toLowerCase();

            // Match by title similarity (minimum 30 chars) or authority + partial title
            const titleMatch =
              procTitle.length > 30 &&
              projectTitle.length > 30 &&
              (procTitle.includes(projectTitle.substring(0, 30)) ||
                projectTitle.includes(procTitle.substring(0, 30)));

            const authorAndTitleMatch =
              procAuthor === projectAuthor &&
              procTitle.length > 15 &&
              projectTitle.length > 15 &&
              (procTitle.includes(projectTitle.substring(0, 15)) ||
                projectTitle.includes(procTitle.substring(0, 15)));

            return titleMatch || authorAndTitleMatch;
          });

          if (isInProject) {
            proceduresInProjects++;
            // Check if procedure is from today
            if (procDate.getTime() === today.getTime()) {
              todayProceduresInProjects++;
            }
          }
        });

        const projectMatchPercentage =
          procedures.length > 0
            ? ((proceduresInProjects / procedures.length) * 100).toFixed(1)
            : 0;

        console.log('Project Statistics:', {
          totalProceduresInProjects: proceduresInProjects,
          todayProceduresInProjects,
          projectMatchPercentage: `${projectMatchPercentage}%`,
        });

        procedures.forEach((proc) => {
          const procDate = new Date(
            proc.publishedDate || proc.publicationDate || proc.createdDate,
          );
          procDate.setHours(0, 0, 0, 0); // Normalize to start of day
          const value = parseFloat(proc.estimatedValue) || 0;

          // NOTE: todayCount is already calculated from API with date filter
          // No need to count here anymore

          // Count procedures from last 7 days
          if (procDate >= weekAgo && procDate <= today) {
            thisWeekCount++;
            totalMarketValue += value;
          }

          // Count procedures from previous week (days 8-14)
          if (procDate >= twoWeeksAgo && procDate < weekAgo) {
            lastWeekCount++;
          }

          // Count by type - using cpvCategory (only field available)
          const type = proc.cpvCategory || 'Altele';
          if (!typeMap[type]) {
            typeMap[type] = { type, count: 0, value: 0 };
          }
          typeMap[type].count++;
          typeMap[type].value += value;

          // NOTE: Direct awards and announcement types (AI/AA) are not available in current API response
          // API only returns: auctionId, auctionTitle, cpvCategory, publishedDate, etc.
          // To detect these, we would need additional fields like 'noticeType' or 'procedureType'

          // Group by authority
          const authority = proc.authorityName || 'Necunoscut';
          if (!authorityMap[authority]) {
            authorityMap[authority] = { name: authority, count: 0, value: 0 };
          }
          authorityMap[authority].count++;
          authorityMap[authority].value += value;
        });

        // Calculate weekly trend. Primary: this 7 days vs previous 7 days from
        // the bigger sample. Fallback when the sample doesn't span 14 days:
        // split the sample by date midpoint and compare per-day publication rates,
        // so the pill always shows a real percentage rather than an empty state.
        const weeklyTrend = (() => {
          if (lastWeekCount > 0) {
            return parseFloat(
              (((thisWeekCount - lastWeekCount) / lastWeekCount) * 100).toFixed(1),
            );
          }

          const dated = procedures
            .map((p) => ({
              t: new Date(
                p.publishedDate || p.publicationDate || p.createdDate,
              ).getTime(),
            }))
            .filter((p) => Number.isFinite(p.t))
            .sort((a, b) => b.t - a.t);

          if (dated.length < 10) return 0;

          const newestMs = dated[0].t;
          const oldestMs = dated[dated.length - 1].t;
          const spanMs = newestMs - oldestMs;
          if (spanMs <= 0) return 0;

          const midMs = oldestMs + spanMs / 2;
          const recent = dated.filter((p) => p.t >= midMs);
          const older = dated.filter((p) => p.t < midMs);
          if (older.length === 0) return 0;

          // Rate per day so unequal half-spans don't bias the comparison.
          const dayMs = 1000 * 60 * 60 * 24;
          const recentRate = recent.length / Math.max(1, (newestMs - midMs) / dayMs);
          const olderRate = older.length / Math.max(1, (midMs - oldestMs) / dayMs);
          if (olderRate === 0) return 0;

          return parseFloat(
            (((recentRate - olderRate) / olderRate) * 100).toFixed(1),
          );
        })();

        console.log('Weekly Trend Calculation:', {
          thisWeekCount,
          lastWeekCount,
          weeklyTrend: `${weeklyTrend}%`,
          calculation:
            lastWeekCount > 0
              ? `((${thisWeekCount} - ${lastWeekCount}) / ${lastWeekCount} * 100)`
              : 'No previous week data',
        });

        // Use the real total from the API envelope ({results, totalResults}) — projects.length
        // is capped at pageSize=50 and would misreport the number of identified projects.
        const projectsTotal =
          typeof projectsResponse?.totalResults === 'number'
            ? projectsResponse.totalResults
            : projects.length;

        setDashboardData({
          todayProcedures: todayCount,
          procedures7d: thisWeekCount,
          totalMarketValue: totalMarketValue,
          weeklyTrend,
          activeProjects: projectsTotal,
          loading: false,
          proceduresInProjects,
          todayProceduresInProjects,
          projectMatchPercentage: parseFloat(projectMatchPercentage),
        });

        setRecentProcedures(procedures.slice(0, 20));
        setProceduresByType(
          Object.values(typeMap).sort((a, b) => b.count - a.count),
        );
        setTopAuthorities(
          Object.values(authorityMap)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10),
        );

        // Calculate regional statistics
        const regionMap = {};
        procedures.forEach((proc) => {
          const county = proc.county || 'Necunoscut';
          if (!regionMap[county]) {
            regionMap[county] = { region: county, count: 0, value: 0 };
          }
          regionMap[county].count++;
          regionMap[county].value += parseFloat(proc.estimatedValue) || 0;
        });
        setRegionalStats(
          Object.values(regionMap).sort((a, b) => b.count - a.count),
        );

        // Generate 7-day trend chart data
        const trendData = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('ro-RO', {
            day: '2-digit',
            month: '2-digit',
          });

          const dayCount = procedures.filter((proc) => {
            const procDate = new Date(
              proc.publishedDate || proc.publicationDate || proc.createdDate,
            );
            procDate.setHours(0, 0, 0, 0);
            return procDate.getTime() === date.getTime();
          }).length;

          trendData.push({
            date: dateStr,
            proceduri: dayCount,
          });
        }
        setTrendChartData(trendData);

        console.log('=== FINAL STATE ===');
        console.log('Dashboard Data:', {
          todayProcedures: todayCount,
          totalMarketValue,
          weeklyTrend: parseFloat(weeklyTrend),
          activeProjects: projects.length,
          proceduresInProjects,
          todayProceduresInProjects,
          projectMatchPercentage: `${projectMatchPercentage}%`,
          note: 'Direct awards and announcement types not available in API response',
        });
        console.log('Date Calculation:', {
          today: today.toLocaleDateString('ro-RO'),
          weekAgo: weekAgo.toLocaleDateString('ro-RO'),
          twoWeeksAgo: twoWeeksAgo.toLocaleDateString('ro-RO'),
          thisWeekCount,
          lastWeekCount,
        });
        console.log('Trend Chart Data:', trendData);
        console.log(
          'Procedures By Type (Top 5):',
          Object.values(typeMap).slice(0, 5),
        );
        console.log(
          'Top Authorities (Top 5):',
          Object.values(authorityMap).slice(0, 5),
        );
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error details:', error.message);
        console.error('Error stack:', error.stack);
      } finally {
        setLoadingData(false);
        console.log('Loading finished, setLoadingData(false) called');
      }
    };

    console.log('useEffect triggered - calling fetchDashboardData');
    fetchDashboardData();

    // Refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(2)} MLD`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)} M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)} K`;
    return value.toFixed(0);
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div
            className="w-8 h-8 border-4 border-gray-100 border-t-gray-400 rounded-full animate-spin"
            style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}
          ></div>
        </div>
      </div>
    </div>
  );

  const KPICard = ({
    icon: Icon,
    label,
    value,
    trend,
    trendLabel,
    color = 'blue',
    loading,
    link,
  }) => {
    const colorMap = {
      blue: {
        bg: 'from-blue-600 to-blue-700',
        icon: 'text-blue-100',
        border: 'border-blue-400/30',
      },
      green: {
        bg: 'from-emerald-600 to-green-700',
        icon: 'text-emerald-100',
        border: 'border-emerald-400/30',
      },
      purple: {
        bg: 'from-purple-600 to-purple-700',
        icon: 'text-purple-100',
        border: 'border-purple-400/30',
      },
      orange: {
        bg: 'from-orange-600 to-orange-700',
        icon: 'text-orange-100',
        border: 'border-orange-400/30',
      },
      red: {
        bg: 'from-red-600 to-red-700',
        icon: 'text-red-100',
        border: 'border-red-400/30',
      },
      indigo: {
        bg: 'from-indigo-600 to-indigo-700',
        icon: 'text-indigo-100',
        border: 'border-indigo-400/30',
      },
    };

    const CardContent = (
      <div
        className={`bg-gradient-to-br ${colorMap[color].bg} border ${colorMap[color].border} rounded-lg p-4 sm:p-6 hover:shadow-2xl transition-all h-full min-h-[180px] flex flex-col justify-between overflow-hidden ${link ? 'cursor-pointer hover:scale-105' : ''}`}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="p-3 rounded-xl bg-black/40 backdrop-blur-sm">
            <Icon className={`w-8 h-8 ${colorMap[color].icon}`} />
          </div>
        </div>

        {/* Fixed-height bands so all 4 cards align label, value, and sublabel on the same rows. */}
        <div className="text-center flex-1 flex flex-col justify-center min-w-0 gap-2">
          <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold min-h-[2.5rem] flex items-center justify-center leading-tight">
            {label}
          </div>
          {loading ? (
            <div className="h-10 bg-gray-700/50 animate-pulse rounded w-28 mx-auto"></div>
          ) : (
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight font-mono break-words leading-tight min-h-[2rem] flex items-center justify-center">
              {value}
            </div>
          )}
          <div className="text-xs text-gray-500 min-h-[1.25rem] leading-tight">
            {trendLabel || ' '}
          </div>
        </div>
      </div>
    );

    return link ? <Link to={link} className="block h-full">{CardContent}</Link> : CardContent;
  };

  // SEO prerender setup
  useEffect(() => {
    if (typeof window !== 'undefined') window.prerenderReady = false;
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <SeoPageComponent
        pageTitle={`${import.meta.env.VITE_NAME} | Dashboard Licitații Publice România - Date Live SEAP`}
        pageDescription="Monitor live licitațiile publice din România. Date în timp real despre proceduri SEAP, proiecte, autorități contractante și statistici piață."
        pageKeywords="dashboard licitații, SEAP live, statistici achiziții publice, proceduri active, piață licitații România"
        pageUrl={import.meta.env.VITE_DOMAIN}
      />

      <div className="min-h-screen bg-gray-50">
        {/* HEADER STATS BAR - KPI-uri principale NYSE Style */}
        <div className="bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 border-b border-gray-800 py-6 shadow-2xl">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                icon={FileText}
                label="Proceduri active"
                value={dashboardData.procedures7d}
                trendLabel="ultimele 7 zile"
                color="blue"
                loading={loadingData}
                link="/licitatii"
              />
              <KPICard
                icon={DollarSign}
                label="Valoare ultimele 7 zile (estim.)"
                value={`${formatCurrency(dashboardData.totalMarketValue)} RON`}
                color="green"
                loading={loadingData}
              />
              <KPICard
                icon={Activity}
                label="Trend publicare"
                value={`${dashboardData.weeklyTrend > 0 ? '+' : ''}${dashboardData.weeklyTrend}%`}
                trend={dashboardData.weeklyTrend}
                trendLabel="ritm vs perioada anterioară"
                color="purple"
                loading={loadingData}
              />
              <KPICard
                icon={Folder}
                label="Proiecte Identificate"
                value={dashboardData.activeProjects}
                color="orange"
                loading={loadingData}
                link="/proiecte"
              />
            </div>
          </div>
        </div>

        {/* PERSUASIVE CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 border-b border-blue-800">
          <div className="container max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-white">
                <span className="text-2xl">🚀</span>
                <div>
                  <p className="text-sm font-bold">
                    Economisești 10+ ore/săptămână cu automatizare completă
                  </p>
                  <p className="text-xs text-blue-100">
                    Peste 150+ companii folosesc Licitatii-Constructii pentru monitorizare
                    licitații
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                {!isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(true)}
                    className="bg-white text-blue-600 px-6 py-2 rounded-lg font-bold text-sm hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap"
                  >
                    Vreau invitație →
                  </button>
                )}
                <Link
                  to="/licitatii"
                  className="bg-blue-800 text-white px-6 py-2 rounded-lg font-semibold text-sm hover:bg-blue-900 transition-colors border border-blue-600 whitespace-nowrap"
                >
                  Exploreaza Licitatii
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN CONTENT - 2 Columns Layout */}
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* LEFT COLUMN - Recent Procedures Table (2/3 width) */}
            <div className="lg:col-span-2 min-w-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">
                    Ultimele Proceduri Publicate
                  </h2>
                  <Link
                    to="/licitatii"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Vezi toate →
                  </Link>
                </div>
                <div className="overflow-x-auto">
                  {loadingData ? (
                    <LoadingSpinner />
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Tip
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Procedură
                          </th>
                          <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Autoritate
                          </th>
                          <th className="hidden sm:table-cell px-2 sm:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Valoare
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Data
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {recentProcedures.slice(0, 15).map((proc, index) => (
                          <tr
                            key={proc.auctionId || proc.noticeNo || index}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-2 sm:px-4 py-3">
                              <span className="inline-block px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                                {proc.cpvCategory ||
                                  proc.noticeEntityType ||
                                  'CN'}
                              </span>
                            </td>
                            <td className="px-2 sm:px-4 py-3">
                              <Link
                                to={`/licitatii/${slugify(proc.auctionTitle || proc.contractTitle)}/${proc.mongoId}`}
                                className="text-gray-900 hover:text-blue-600 font-medium line-clamp-2 text-xs sm:text-sm"
                              >
                                {proc.auctionTitle ||
                                  proc.contractTitle ||
                                  'Fără titlu'}
                              </Link>
                            </td>
                            <td className="hidden md:table-cell px-4 py-3 text-gray-600 max-w-xs truncate">
                              {proc.authorityName || 'N/A'}
                            </td>
                            <td className="hidden sm:table-cell px-2 sm:px-4 py-3 text-right font-semibold text-gray-900">
                              {proc.estimatedValue
                                ? `${formatCurrency(proc.estimatedValue)} RON`
                                : '-'}
                            </td>
                            <td className="px-2 sm:px-4 py-3 text-center text-gray-500 text-xs">
                              {new Date(
                                proc.publishedDate ||
                                  proc.publicationDate ||
                                  proc.createdDate,
                              ).toLocaleDateString('ro-RO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit',
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* SECTION BELOW TABLE - Project Statistics & Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
                {/* Project Statistics with Donut Chart */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg shadow-sm border border-green-200 p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">📊</span>
                    <h3 className="text-base font-bold text-gray-900">
                      Raport Proiecte
                    </h3>
                  </div>
                  {loadingData ? (
                    <div className="space-y-2">
                      <div className="animate-pulse h-32 bg-green-200 rounded"></div>
                      <div className="animate-pulse h-6 bg-green-200 rounded"></div>
                    </div>
                  ) : (
                    <div>
                      {/* Donut Chart */}
                      <div className="mb-4">
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={[
                                {
                                  name: 'În Proiecte',
                                  value:
                                    dashboardData.proceduresInProjects || 0,
                                  fill: '#10b981',
                                },
                                {
                                  name: 'Standalone',
                                  value:
                                    proceduresByType.reduce(
                                      (sum, t) => sum + t.count,
                                      0,
                                    ) -
                                    (dashboardData.proceduresInProjects || 0),
                                  fill: '#e5e7eb',
                                },
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              <Cell key="cell-0" fill="#10b981" />
                              <Cell key="cell-1" fill="#e5e7eb" />
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '12px',
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="text-center -mt-2">
                          <div className="text-3xl font-bold text-green-700">
                            {dashboardData.projectMatchPercentage}%
                          </div>
                          <p className="text-xs text-gray-600">în proiecte</p>
                        </div>
                      </div>

                      {/* Statistics */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center py-2 border-t border-green-200">
                          <span className="text-gray-600">
                            Proceduri în proiecte:
                          </span>
                          <span className="font-bold text-gray-900">
                            {dashboardData.proceduresInProjects || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-t border-green-200">
                          <span className="text-gray-600">
                            Astăzi în proiecte:
                          </span>
                          <span className="font-bold text-green-700">
                            {dashboardData.todayProceduresInProjects || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-t border-green-200">
                          <span className="text-gray-600">
                            Total proiecte active:
                          </span>
                          <span className="font-bold text-gray-900">
                            {dashboardData.activeProjects}
                          </span>
                        </div>
                      </div>
                      <Link
                        to="/proiecte"
                        className="mt-4 block w-full bg-green-600 text-white text-center font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Vezi Toate Proiectele →
                      </Link>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-sm p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 text-8xl opacity-10">
                    🤖
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl sm:text-3xl">🤖</span>
                      <h3 className="text-base sm:text-lg font-bold">
                        Începe Monitorizarea
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-blue-100 mb-4">
                      Configurează alerte personalizate și primești notificări
                      automate pentru procedurile care te interesează.
                    </p>
                    {isAuthenticated ? (
                      <Link
                        to="/proceduri-salvate"
                        className="block w-full bg-white text-blue-600 text-center font-bold py-2 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-blue-50 transition-all hover:shadow-lg text-sm sm:text-base"
                      >
                        Vezi procedurile mele
                      </Link>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowInviteModal(true)}
                          className="block w-full bg-white text-blue-600 text-center font-bold py-2 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-blue-50 transition-all hover:shadow-lg text-sm sm:text-base"
                        >
                          🎯 Vreau invitație
                        </button>
                        <p className="text-xs text-blue-200 mt-3 text-center">
                          ✓ Setup în 2 minute • ✓ 100 credite incluse
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Stats & Quick Info (1/3 width) */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Procedure Types Distribution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900">
                    Distribuție Tipuri Proceduri
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    pe ultimele 100 proceduri publicate
                  </p>
                </div>
                <div className="p-3 sm:p-4 min-h-[200px]">
                  {loadingData ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="space-y-3">
                      {proceduresByType.slice(0, 6).map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-sm text-gray-700 font-medium truncate">
                              {item.type}
                            </span>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm font-bold text-gray-900">
                              {item.count}
                            </div>
                            {item.value > 0 && (
                              <div className="text-xs text-gray-500">
                                {formatCurrency(item.value)} RON
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Top Authorities */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900">
                    Top 10 Autorități Contractante
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    pe ultimele 100 proceduri publicate
                  </p>
                </div>
                <div className="p-3 sm:p-4">
                  {loadingData ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="space-y-2">
                      {topAuthorities.slice(0, 8).map((auth, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <span className="text-xs text-gray-700 truncate">
                              {auth.name}
                            </span>
                          </div>
                          <div className="text-xs font-semibold text-gray-900 ml-2">
                            {auth.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CHARTS SECTION - Full Width Below */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-6 sm:mt-8 lg:mt-10">
            {/* Trend Chart - 7 Days */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-6">
                Trend Proceduri - Ultimele 7 Zile
              </h3>
              {loadingData ? (
                <LoadingSpinner />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="proceduri"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Proceduri Publicate"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Pie Chart - Procedure Types */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-6">
                Distribuție pe Tipuri - Top 5
              </h3>
              {loadingData ? (
                <LoadingSpinner />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={proceduresByType.slice(0, 5)}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => `${entry.type}: ${entry.count}`}
                      labelStyle={{ fontSize: '11px', fontWeight: 'bold' }}
                    >
                      {proceduresByType.slice(0, 5).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            [
                              '#3b82f6',
                              '#10b981',
                              '#f59e0b',
                              '#ef4444',
                              '#8b5cf6',
                            ][index % 5]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Bar Chart - Top Authorities - REMOVED, moved to sidebar */}
          </div>

          {/* REGIONAL STATISTICS & MAP - FULL WIDTH */}
          <div className="mt-6 sm:mt-8 lg:mt-10">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="text-2xl">🗺️</span>
                Distribuție Geografică - Licitații pe Regiuni
              </h3>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Romania Map */}
                <div>{loadingData ? <LoadingSpinner /> : <RomaniaMap />}</div>

                {/* Top Regions Table */}
                <div>
                  <h4 className="text-base font-bold text-gray-900 mb-4">
                    Top 10 Regiuni - Număr Proceduri
                  </h4>
                  {loadingData ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="space-y-3">
                      {regionalStats.slice(0, 10).map((region, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h5 className="text-sm font-bold text-gray-900 truncate">
                                  {region.region}
                                </h5>
                                <p className="text-xs text-gray-600">
                                  Valoare: {formatCurrency(region.value)} RON
                                </p>
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-xl font-bold text-blue-600">
                                {region.count}
                              </div>
                              <div className="text-xs text-gray-500">
                                proceduri
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Regional Bar Chart */}
                  <div className="mt-6">
                    <h4 className="text-base font-bold text-gray-900 mb-4">
                      Valoare Licitații - Top 8 Regiuni
                    </h4>
                    {loadingData ? (
                      <LoadingSpinner />
                    ) : regionalStats.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={regionalStats.slice(0, 8)}
                          layout="vertical"
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                          />
                          <XAxis
                            type="number"
                            stroke="#6b7280"
                            style={{ fontSize: '10px' }}
                            tickFormatter={(value) => formatCurrency(value)}
                          />
                          <YAxis
                            dataKey="region"
                            type="category"
                            stroke="#6b7280"
                            style={{ fontSize: '10px' }}
                            width={100}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '12px',
                            }}
                            formatter={(value) => [
                              `${formatCurrency(value)} RON`,
                              'Valoare Totală',
                            ]}
                          />
                          <Bar
                            dataKey="value"
                            fill="#3b82f6"
                            radius={[0, 8, 8, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>Nu există date disponibile</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SMALL CTA BANNER */}
          <div className="mt-6 sm:mt-8 lg:mt-10">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 sm:p-6 lg:p-8 text-white shadow-lg">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">⚡</span>
                  <div className="text-sm">
                    <p className="font-bold">
                      Vrei rapoarte AI personalizate pentru fiecare licitație?
                    </p>
                    <p className="text-orange-100 text-xs">
                      Începe cu planul Professional - 99 RON/lună
                    </p>
                  </div>
                </div>
                <Link
                  to="/abonamente"
                  className="bg-white text-orange-600 px-6 py-2 rounded-lg font-bold text-sm hover:bg-orange-50 transition-all whitespace-nowrap shadow-lg"
                >
                  Vezi Planuri →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* COMPACT CTA SECTION */}
        {!isAuthenticated && (
          <div className="container max-w-7xl mx-auto px-4 py-12">
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 rounded-2xl shadow-xl p-10 text-center text-white">
              <h3 className="text-2xl font-bold mb-2">
                Gata să automatizezi monitorizarea licitațiilor?
              </h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Peste 150+ companii economisesc 10+ ore/săptămână cu Licitatii-Constructii.
                Cere invitația ta astăzi.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(true)}
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-all hover:shadow-lg transform hover:scale-105"
                >
                  🚀 Vreau invitație
                </button>
                <Link
                  to="/abonamente"
                  className="bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors border border-blue-500"
                >
                  Vezi Planuri & Prețuri
                </Link>
              </div>
              <p className="text-xs text-blue-200 mt-4">
                ✓ Setup în 2 minute • ✓ Anulare oricând • ✓ Suport 24/7
              </p>
            </div>
          </div>
        )}
      </div>

      <InviteFormModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </>
  );
};

export default Home;
